import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Modal,
  Popconfirm,
  Space,
  List,
  Table,
  TableColumnProps,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import SITELINKS from 'constants/sitelinks';
import { authProfile } from 'redux/auth/selectors';
import { useDrivers } from 'services/DriverService';
import { useDismantleJourney } from 'services/journeys';
import {
  JourneyTemplate,
  TemplateAssignment,
  useJourneyTemplateLogs,
  assignTemplateDriver,
  instantiateTemplate,
  useDeleteJourneyTemplate,
  useJourneyTemplates,
  useRenameJourneyTemplate,
} from 'services/journeyTemplates';
import { highlightMenuItem } from '../lastDraft';

const { Text, Title } = Typography;

const EVENT_LABELS: Record<string, string> = {
  created: 'Creada',
  renamed: 'Renombrada',
  deleted: 'Borrada',
  used: 'Usada para crear una ruta',
};

const TemplateHistory: React.FC<{ templateId: number }> = ({ templateId }) => {
  const { data: logs, isLoading, error } = useJourneyTemplateLogs(templateId);
  if (error) {
    return <Alert type="error" showIcon message={(error as Error).message} />;
  }
  return (
    <List
      loading={isLoading}
      dataSource={logs ?? []}
      locale={{ emptyText: 'Sin historial' }}
      renderItem={(log) => {
        const d = (log.details ?? {}) as Record<
          string,
          string | number | undefined
        >;
        const detail =
          log.event === 'renamed'
            ? `de “${d.previousName}” a “${d.name}”`
            : log.event === 'created'
              ? `desde la ruta ${d.sourceJourneyId}, ${d.items} ${
                  d.items === 1 ? 'punto' : 'puntos'
                }`
              : null;
        return (
          <List.Item>
            <Space direction="vertical" size={0}>
              <Space size={8}>
                <Text strong>{EVENT_LABELS[log.event] ?? log.event}</Text>
                {log.event === 'used' && d.journeyId ? (
                  <Link to={`${SITELINKS.journey}/${d.journeyId}`}>
                    {String(d.journeyId)}
                  </Link>
                ) : null}
                {detail && <Text type="secondary">{detail}</Text>}
              </Space>
              <Text type="secondary">
                {dayjs(log.createdAt).format('DD/MM/YYYY HH:mm')}
                {log.performedBy ? ` · ${log.performedBy}` : ''}
              </Text>
            </Space>
          </List.Item>
        );
      }}
    />
  );
};

const JourneyTemplates: React.FC = () => {
  const history = useHistory();
  const centerId = useSelector(authProfile)?.paquerypoint?.id as
    | number
    | undefined;
  const { data: templates, isLoading, error } = useJourneyTemplates();
  const { data: drivers } = useDrivers();
  const { mutate: rename } = useRenameJourneyTemplate();
  const { mutate: remove, isPending: isRemoving } = useDeleteJourneyTemplate();
  const { mutateAsync: dismantle } = useDismantleJourney();
  const [modal, modalHolder] = Modal.useModal();

  const [chosen, setChosen] = useState<JourneyTemplate | null>(null);
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [creating, setCreating] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [historyOf, setHistoryOf] = useState<JourneyTemplate | null>(null);

  const driverName = (template: JourneyTemplate): string => {
    if (!template.driverId) return '-';
    const driver = (drivers ?? []).find(
      (d: { id: number }) => d.id === template.driverId,
    ) as { name?: string; lastName?: string } | undefined;
    if (driver) return `${driver.name ?? ''} ${driver.lastName ?? ''}`.trim();
    return template.driverName || `Driver #${template.driverId}`;
  };

  const openDraft = (journeyId: string, suggestedDriverId: number | null) => {
    highlightMenuItem('journeysPrep');
    history.push(
      `${SITELINKS.journey}/${journeyId}${
        suggestedDriverId ? `?suggestedDriver=${suggestedDriverId}` : ''
      }`,
    );
  };

  const create = async () => {
    if (!chosen || !centerId) return;
    setCreating(true);
    setFailed(null);
    try {
      const result = await instantiateTemplate(chosen, date, centerId);
      const template = chosen;
      setChosen(null);
      const assignment: TemplateAssignment = template.driverId
        ? await assignTemplateDriver(result.journeyId, template.driverId)
        : { ok: true };
      const open = () =>
        openDraft(
          result.journeyId,
          assignment.noPlate ? template.driverId : null,
        );
      if (result.skipped.length === 0 && assignment.ok) {
        open();
        return;
      }
      modal.confirm({
        title: `La ruta ${result.journeyId} se creó con avisos`,
        content: (
          <Space direction="vertical" size={4}>
            {result.skipped.length > 0 && (
              <Text>
                Sin{' '}
                {result.skipped.length === 1
                  ? 'un punto'
                  : `${result.skipped.length} puntos`}
                :
              </Text>
            )}
            {result.skipped.map((item) => (
              <Text key={item.storeName}>
                <b>{item.storeName}</b>: {item.reason}
              </Text>
            ))}
            {!assignment.ok && (
              <Text>
                <b>{driverName(template)}</b> no quedó asignado:{' '}
                {assignment.reason}
                {assignment.activeJourneyId && (
                  <>
                    {' '}
                    La ruta activa es{' '}
                    <Link
                      to={`${SITELINKS.journey}/${assignment.activeJourneyId}`}
                    >
                      {assignment.activeJourneyId}
                    </Link>
                    .
                  </>
                )}
              </Text>
            )}
          </Space>
        ),
        okText: assignment.ok ? 'Abrir la ruta' : 'Abrirla sin driver',
        cancelText: 'Desarmarla',
        onOk: open,
        onCancel: () => dismantle(result.journeyId),
      });
    } catch (err) {
      // Si el backend no armó la ruta (409) ya la desarmó él: queda su motivo
      // en el modal, sin asignar driver ni abrir nada.
      setFailed((err as Error)?.message ?? 'No se pudo crear la ruta');
    } finally {
      setCreating(false);
    }
  };

  const columns: TableColumnProps<JourneyTemplate>[] = [
    {
      title: 'Nombre',
      render: (template: JourneyTemplate) => (
        <Text
          strong
          editable={{
            maxLength: 60,
            tooltip: 'Cambiar el nombre',
            onChange: (value) => {
              const name = value.trim();
              if (name && name !== template.name)
                rename({ id: template.id, name });
            },
          }}
        >
          {template.name}
        </Text>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Puntos',
      width: 110,
      align: 'center',
      render: (template: JourneyTemplate) => template.items.length,
    },
    {
      title: 'Driver sugerido',
      render: driverName,
    },
    {
      title: 'Creada',
      width: 190,
      render: (template: JourneyTemplate) => (
        <Space direction="vertical" size={0}>
          <span>{dayjs(template.createdAt).format('DD/MM/YYYY HH:mm')}</span>
          {template.createdBy && (
            <Text type="secondary">{template.createdBy}</Text>
          )}
        </Space>
      ),
    },
    {
      align: 'right',
      width: 270,
      render: (template: JourneyTemplate) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setDate(dayjs());
              setFailed(null);
              setChosen(template);
            }}
          >
            Crear ruta
          </Button>
          <Tooltip title="Historial">
            <Button
              size="small"
              icon={<HistoryOutlined />}
              onClick={() => setHistoryOf(template)}
            />
          </Tooltip>
          <Popconfirm
            title="¿Borrar la plantilla?"
            description="Las rutas que se crearon con ella no cambian."
            okText="Borrar"
            cancelText="Cancelar"
            placement="topRight"
            disabled={isRemoving}
            onConfirm={() => remove(template.id)}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      {modalHolder}
      <Modal
        open={!!historyOf}
        title={historyOf ? `Historial de “${historyOf.name}”` : ''}
        footer={null}
        onCancel={() => setHistoryOf(null)}
        destroyOnClose
      >
        {historyOf && <TemplateHistory templateId={historyOf.id} />}
      </Modal>
      <Title level={4} style={{ marginTop: 0 }}>
        Plantillas de rutas de colectas
      </Title>
      <Text type="secondary">
        Una plantilla es una ruta de colectas guardada tal cual se armó. Se
        guardan desde el borrador de una ruta, con el botón “Guardar como
        plantilla”.
      </Text>
      {error && (
        <Alert
          type="error"
          showIcon
          message="No se pudieron leer las plantillas"
          description={(error as Error).message}
          style={{ marginTop: 16 }}
        />
      )}
      <Table
        style={{ marginTop: 16 }}
        size="middle"
        rowKey={(template) => template.id}
        loading={isLoading}
        dataSource={templates ?? []}
        columns={columns}
        pagination={false}
        locale={{ emptyText: 'Todavía no hay plantillas' }}
        expandable={{
          expandedRowRender: (template) => (
            <Space direction="vertical" size={4}>
              {template.items.map((item, index) => (
                <Space key={`${item.collectionPointId}-${item.scheduledStart}`}>
                  <Tag>{index + 1}</Tag>
                  <Text strong>
                    {dayjs(item.scheduledStart).format('HH:mm')}
                  </Text>
                  <span>{item.storeName}</span>
                  <Text type="secondary">
                    {item.estimatedDurationMinutes} min
                    {item.address ? ` · ${item.address}` : ''}
                  </Text>
                </Space>
              ))}
            </Space>
          ),
        }}
      />

      <Modal
        open={!!chosen}
        title={chosen ? `Crear ruta desde “${chosen.name}”` : ''}
        okText="Crear ruta"
        cancelText="Cancelar"
        confirmLoading={creating}
        closable={!creating}
        maskClosable={!creating}
        cancelButtonProps={{ disabled: creating }}
        onOk={create}
        onCancel={() => setChosen(null)}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Text>
            Se arma un borrador con {chosen?.items.length ?? 0} colectas para el
            día que elijas, cada una a la hora de la plantilla. El driver queda
            sugerido: lo asignás en el borrador.
          </Text>
          <DatePicker
            value={date}
            allowClear={false}
            format="DD/MM/YYYY"
            disabledDate={(day) => day.isBefore(dayjs().startOf('day'))}
            style={{ width: '100%' }}
            onChange={(value) => value && setDate(value)}
          />
          {failed && <Alert type="error" showIcon message={failed} />}
        </Space>
      </Modal>
    </Card>
  );
};

export default JourneyTemplates;
